// Builds a printable HTML report of the hotel rows. We render to PDF through the browser's own
// print engine ("Save as PDF") rather than a bundled PDF library — it needs no dependency, it
// paginates a long table for free (the <thead> repeats on every page), and the output respects
// the user's paper size. The screen writes this HTML into a hidden iframe and calls print().
//
// The columns are the SAME set as the CSV export (via hotelToRow), so the two formats always
// carry identical data — only the presentation differs.

import { CSV_HEADERS, hotelToRow } from "./hotelCsv";

// Escape the five HTML-significant characters so a hotel name with & < > " ' can never break
// the markup (or inject anything into the printed document).
const esc = (v) =>
  String(v ?? "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));

const fmtDateTime = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
};

/**
 * @param {Array} rows  UI hotels (mapApiHotelToUi shape)
 * @param {{ title?:string, subtitle?:string, filterSummary?:string, generatedAt?:Date }} meta
 * @returns {string} a complete, self-contained HTML document
 */
export function hotelsToPrintableHtml(rows = [], meta = {}) {
  const {
    title = "Hotels",
    subtitle = "",
    filterSummary = "",
    generatedAt = new Date(),
  } = meta;

  const headCells = CSV_HEADERS.map((h) => `<th>${esc(h)}</th>`).join("");
  const bodyRows = rows.length
    ? rows
        .map((h) => `<tr>${hotelToRow(h).map((c) => `<td>${esc(c)}</td>`).join("")}</tr>`)
        .join("")
    : `<tr><td class="empty" colspan="${CSV_HEADERS.length}">No hotels to export.</td></tr>`;

  const count = rows.length;
  const metaLine = [
    `${count.toLocaleString("en-GB")} hotel${count === 1 ? "" : "s"}`,
    filterSummary && `Filters: ${filterSummary}`,
    `Generated ${fmtDateTime(generatedAt)}`,
  ]
    .filter(Boolean)
    .map(esc)
    .join("&nbsp;&nbsp;·&nbsp;&nbsp;");

  // Landscape by default — nine columns don't fit portrait. Header repeats per page; rows
  // avoid breaking mid-cell.
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${esc(title)}</title>
<style>
  @page { size: A4 landscape; margin: 14mm 12mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #1f2937; margin: 0; }
  .report-head { border-bottom: 3px solid #f59e0b; padding-bottom: 10px; margin-bottom: 14px; }
  .report-title { font-size: 20px; font-weight: 800; margin: 0; color: #111827; }
  .report-title span { color: #f59e0b; }
  .report-sub { font-size: 12px; color: #6b7280; margin: 3px 0 0; }
  .report-meta { font-size: 11px; color: #6b7280; margin: 6px 0 0; }
  table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
  thead { display: table-header-group; }   /* repeat the header on every printed page */
  th { text-align: left; background: #f3f4f6; color: #374151; font-weight: 700;
       padding: 7px 8px; border-bottom: 1.5px solid #e5e7eb; text-transform: uppercase;
       letter-spacing: .3px; font-size: 9.5px; }
  td { padding: 6px 8px; border-bottom: 1px solid #f0f1f3; vertical-align: top; }
  tr { page-break-inside: avoid; }
  tbody tr:nth-child(even) { background: #fafafa; }
  td.empty { text-align: center; color: #9ca3af; padding: 24px; font-size: 12px; }
</style>
</head>
<body>
  <div class="report-head">
    <h1 class="report-title">Sun<span>Sky</span> — ${esc(title)}</h1>
    ${subtitle ? `<p class="report-sub">${esc(subtitle)}</p>` : ""}
    <p class="report-meta">${metaLine}</p>
  </div>
  <table>
    <thead><tr>${headCells}</tr></thead>
    <tbody>${bodyRows}</tbody>
  </table>
</body>
</html>`;
}
